import EmojiPicker, { EmojiClickData, EmojiStyle } from "emoji-picker-react";
import { PostReaction } from "models/postReaction";
import { useEffect, useRef, useState } from "react";
import { AppLog } from "services";

import { StatusMsg } from "utils";
import { favorite, profileVerified, share } from "assets";
import { postedTimeAgo } from "utils/formatTimeSecondToHour";
import { faFaceSmileBeam, faHeart as solidHeart } from "@fortawesome/free-solid-svg-icons";
import { faHeart as outlineHeart } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ShimmerPostDetail } from "components/app/shrimmer/Shrimmer";

import ArrowUpDown from "pages/profile-post-contents-page-view/ArrowUpDown";
import AudioPlayer from "components/app/post-content-audio-player/AudioPlayer ";
import PhotoPostContent from "components/app/photo-post-content/PhotoPostContent";
import AlbumPostContent from "components/app/album-post-content/AlbumPostContent";
import VideoPostContent from "components/app/video-post-content/VideoPostContent";
import StatusPostContent from "components/app/status-post-content/StatusPostContent";
import SearchParamsNotFound from "components/app/search-params-not-found/SearchParamsNotFound";
import HelmetHtml from "components/app/helmet-html/HelmetHtml";
import ImageUrlProfile from "components/app/image-url/ImageUrlProfile";
import LinkifyText from "components/app/linkify/LinkifyText";
import NewFeedDailogShare from "components/app/new-feed-post/new-feed-components/new-feed-dialogs/NewFeedDialogShare";
import CopyLinkButton from "pages/profile-post-contents-page-view/post-component/CopyLinkButton";
import TextReaction from "pages/profile-post-contents-page-view/post-component/TextReaction";
import DialogPostReaction from "components/app/dialog/DialogPostReaction";
import Comment from "pages/profile-post-contents-page-view/post-component/Comment";
import { Textarea } from "components/ui/textarea";
import { Service } from "utils/services";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { Ellipsis, SendHorizontalIcon, Sticker, X } from "lucide-react";
import StickerPicker from "pages/profile-post-contents-page-view/post-component/Stickers";
import { useViewPostContentStore } from "store/useViewPostContentStore";
import ButtonFollow from "components/app/button-follow/ButtonFollow";
import PostOption from "components/app/post-option/PostOption";
import NewFeedDailogBlock from "components/app/new-feed-post/new-feed-components/new-feed-dialogs/NewFeedDialogBlock";
import NewFeedDailogDeletePost from "components/app/new-feed-post/new-feed-components/new-feed-dialogs/NewFeedDialogDeletePost";
import NewFeedDialogReportPost from "components/app/new-feed-post/new-feed-components/new-feed-dialogs/NewFeedDialogReportPost";
import { useInView } from "react-intersection-observer";
import NewFeedDialogBoostPost from "components/app/new-feed-post/new-feed-components/new-feed-dialogs/NewFeedDialogBoostPost";
import appStore from "store/appStore";
import LivePostContent from "components/app/live-post-content/LivePostConent";
import ViewPostContentLinkButton from "./components/ViewPostContentLikeButton";
import LivePostContentFlv from "components/app/live-post-content/LivePostContentFlv";

interface ViewPostContentProps {
  postId: string | null;
  onClose: () => void;
}

export default function ViewPostContent({ postId, onClose }: ViewPostContentProps) {
  // * --- Variables --- * //
  const {
    posts,
    postsRecord,
    comments,
    commentsRecord,
    currentIndex,
    initializePage,
    onHandleDown,
    onHandleUp,
    fetchComments,
    requestReactionComment,
    requestReplyComment,
    requestSubmitComment,
    fetchSubComments,
    onClickedLike,
    onClickedShare,
    onClickedFollow,
    onClickedFavorite,
    disposedPage,
  } = useViewPostContentStore();

  const [dialogShare, setDialogShare] = useState({
    isOpen: false,
    post: null,
  });

  const [dialogPostReaction, setDialogPostReaction] = useState({
    isOpen: false,
    post: null,
    postReaction: PostReaction.like,
  });

  const scrollRef = useRef<HTMLDivElement>(null);
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [textComment, setTextComment] = useState("");
  const [sticker, setSticker] = useState<any>({});
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showStickerPicker, setShowStickerPicker] = useState(false);
  const stickerPickerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const { ref: inViewRef, inView } = useInView({ threshold: 0.6 });

  // * --- Functions --- * //
  const onOpenDialogShare = (post: any) => {
    try {
      if (Service.isAuthenticated === false) {
        appStore.getState().onOpenDialogWelcome();
        return;
      }

      setDialogShare({
        isOpen: true,
        post: post,
      });
    } catch (error) {
      AppLog.show({
        message: `[onOpenDialogShare]`,
        status: StatusMsg.error,
        error,
      });
    }
  };

  const onOpenReactionDialog = (post: any, postReaction: PostReaction) => {
    try {
      if (Service.isAuthenticated === false) {
        appStore.getState().onOpenDialogWelcome();
        return;
      }

      setDialogPostReaction({
        isOpen: true,
        post: post,
        postReaction,
      });
    } catch (error) {
      AppLog.show({
        message: `[onOpenReactionDialog]`,
        status: StatusMsg.error,
        error,
      });
    }
  };

  const onHandleScrollComment = async () => {
    if (!scrollRef.current || commentsRecord.isLoadingMore()) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;

    if (scrollTop + clientHeight >= scrollHeight - 10 && commentsRecord.canLoadMore()) {
      fetchComments();
    }
  };

  const handleReplyClick = (commentId: number) => {
    setReplyingTo(commentId);
  };

  const onHandleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (textComment) {
        requestSubmitComment(sticker, textComment);
        setTextComment("");
        setSticker({});
      }
    }
  };

  const onEmojiClick = (emojiData: EmojiClickData) => {
    setShowEmojiPicker(false);
    setTextComment((prev) => prev + emojiData.emoji);
    // setShowEmojiPicker(false);
  };

  // * --- useEffect --- * //

  // New testing
  useEffect(() => {
    if (!postId) return;
    initializePage(postId);
    return () => {
      disposedPage();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId]);

  // * ------------------State and function Dailog--------------------- * //

  const [postOption, setPostOption] = useState<{
    isOpen: boolean;
    buttonRef: HTMLButtonElement | null;
    post: any;
  }>({
    isOpen: false,
    buttonRef: null,
    post: null,
  });

  // Popover Option
  const onOpenPostOption = (e: React.MouseEvent<HTMLButtonElement>, post: any) => {
    setPostOption({
      isOpen: true,
      buttonRef: e.currentTarget,
      post: post,
    });
  };

  const onClosePostOption = () => {
    setPostOption({
      isOpen: false,
      buttonRef: null,
      post: null,
    });
  };

  const [dialogBlock, setDialogBlock] = useState({
    isOpen: false,
    post: null,
    optionKey: "",
  });

  const onOpenDialogBlock = (post: any, optionKey: string) => {
    try {
      setDialogBlock({
        isOpen: true,
        post: post,
        optionKey: optionKey,
      });
    } catch (error) {
      AppLog.show({
        message: `[onOpenDialogBlock]`,
        status: StatusMsg.error,
        error,
      });
    }
  };

  const [dialogDeletePost, setDialogDeletePost] = useState({
    isOpen: false,
    post: null,
  });

  const onOpenDialogDeletePost = (post: any) => {
    try {
      setDialogDeletePost({
        isOpen: true,
        post: post,
      });
    } catch (error) {
      AppLog.show({
        message: `[onOpenDialogDeletePost]`,
        status: StatusMsg.error,
        error,
      });
    }
  };

  const [dialogReportPost, setDialogReportPost] = useState({
    isOpen: false,
    post: null,
  });

  const onOpenDialogReportPost = (post: any) => {
    try {
      setDialogReportPost({
        isOpen: true,
        post: post,
      });
    } catch (error) {
      AppLog.show({
        message: `[onOpenDialogReportPost]`,
        status: StatusMsg.error,
        error,
      });
    }
  };

  const [dialogBoostPost, setDialogBoostPost] = useState({
    isOpen: false,
    post: null,
  });

  const onOpenDialogBoostPost = (post: any) => {
    try {
      setDialogBoostPost({
        isOpen: true,
        post: post,
      });
    } catch (error) {
      AppLog.show({
        message: `[onOpenDialogBoostPost]`,
        status: StatusMsg.error,
        error,
      });
    }
  };

  // * --- END Popover Option --- * //

  // * --- Layouts --- * //

  const postCommentContents = (post: any) => {
    if (post) {
      const userPost = post.re_post_user ? post.re_post_user : post.user;
      return (
        <div className="flex flex-col h-screen relative justify-between bg-white  dark:bg-background pt-4 ">
          <div className="flex flex-col relative space-y-2 pl-4 pr-4">
            <div className="flex justify-between items-center">
              <div className="flex flex-row items-start space-x-2 w-full  ">
                <ImageUrlProfile
                  user={userPost}
                  style={{ width: 50, height: 50 }}
                  className="rounded-full object-cover border shadow-md shadow-gray-300 dark:shadow-none"
                  isLinkProfile={true}
                />
                <div className="flex flex-row justify-between items-center  w-full">
                  <div className="flex flex-col ">
                    <div className="flex items-center space-x-2">
                      <p className=" text-xl font-semibold">{userPost?.name}</p>
                      {userPost?.is_verified === "Yes" ? <img src={profileVerified} alt="" style={{ width: 12, height: 12 }} /> : <></>}
                      {userPost?.friend_status === "Friend" ? null : (
                        <ButtonFollow className="h-[25px]" friendStatus={userPost?.friend_status} onClick={() => onClickedFollow(post, userPost?.id.toString())} />
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <p>
                        {post?.is_sponsored === "Yes"
                          ? "Sponsored"
                          : post?.is_available_suggestion === "Yes"
                          ? "Suggestion for you"
                          : `${postedTimeAgo(post?.provider_created_datetime)}`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {Service.isAuthenticated && (
                <Ellipsis
                  onClick={(e: any) => onOpenPostOption(e, post)}
                  className="w-8 h-8 max-sm:w-6 cursor-pointer max-sm:h-6 bg-gray-600 text-white opacity-70 rounded-full p-1 transition-opacity duration-200"
                />
              )}
            </div>

            {post.media_title && <LinkifyText text={post.media_title} />}

            <ViewPostContentLinkButton post={post} />

            <div className="flex-1 flex-row justify-between w-full">
              <div className="flex flex-row justify-start w-full space-x-2">
                <div className="flex w-8 h-8 cursor-pointer  rounded-full bg-gray-200 text-center items-center justify-center" onClick={() => onClickedLike(post)}>
                  <FontAwesomeIcon
                    icon={`${post?.is_reaction_like}` === "1" ? solidHeart : outlineHeart}
                    size="sm"
                    className={` size-5 ${`${post?.is_reaction_like}` === "1" ? "heart-icon text-red-600 heart-fill" : "heart-outline text-black dark:text-gray-500"}`}
                  />
                </div>
                <div
                  className="flex w-8 h-8 cursor-pointer  rounded-full pb-1 pl-1 pr-1 bg-gray-200 text-center items-center justify-center"
                  onClick={() => {
                    onOpenDialogShare(post);
                  }}
                >
                  <img src={share} alt="" width={25} height={25} />
                </div>

                <div
                  className={`flex w-8 h-8 p-1 cursor-pointer rounded-full ${
                    `${post?.is_reaction_favorite}` === "1" ? "bg-yellow-200" : " bg-gray-200"
                  }  text-center items-center justify-center`}
                  onClick={() => onClickedFavorite(post)}
                >
                  <img src={favorite} alt="Favorite" width={25} height={25} />
                </div>
                <CopyLinkButton endPoint={`post/${post.provider_post_id}`} />

                {post?.re_post_user ? (
                  <div className="flex flex-row rounded-2xl pl-1 pr-2 bg-gray-200 text-center items-center justify-center space-x-1">
                    <ImageUrlProfile
                      user={post?.user}
                      style={{ width: 20, height: 20 }}
                      className="rounded-full object-cover border shadow-md shadow-gray-300 dark:shadow-none"
                      isLinkProfile={true}
                    />
                    <p className="font-bold text-sm">{post?.user?.name}</p>
                    <p className="text-sm">Shared</p>
                  </div>
                ) : (
                  <></>
                )}
              </div>
            </div>
            <TextReaction post={post} onOpenReactionDialog={onOpenReactionDialog} />
          </div>
          {/* Sub comments */}

          <div ref={scrollRef} onScroll={onHandleScrollComment} className="flex-1  dark:bg-background px-4 overflow-y-auto mt-4">
            {post.comment_count > 0 && (
              <p className="font-normal hover:underline cursor-pointer mr-1" onClick={() => onOpenReactionDialog(post, PostReaction.comment)}>
                {post.comment_count} Comments
              </p>
            )}

            {[...comments].map((comment, index) => (
              <Comment
                replyingTo={replyingTo!}
                onReplyClick={() => handleReplyClick(comment.id)}
                key={comment.id}
                comment={comment}
                index={index}
                submitReply={(data: any) => {
                  requestReplyComment(data);
                  handleReplyClick(-1);
                }}
                onClikcReact={() => requestReactionComment({ comment })}
                fetchSubComments={fetchSubComments}
              >
                {comment.sub_comments?.data?.length > 0 && (
                  <div className="">
                    {comment.sub_comments.data.map((subComment: any, subIndex: number) => (
                      <Comment
                        replyingTo={replyingTo!}
                        onReplyClick={() => handleReplyClick(subComment.id)}
                        key={subComment.id}
                        comment={subComment}
                        index={subIndex}
                        submitReply={(data: any) => {
                          requestReplyComment(data);
                          handleReplyClick(-1);
                        }}
                        onClikcReact={() =>
                          requestReactionComment({
                            comment: subComment,
                          })
                        }
                        fetchSubComments={fetchSubComments}
                        isSubComment={true}
                      >
                        {/* Recursively render deeper sub-comments */}
                        {subComment.sub_comments?.data?.length > 0 && (
                          <div className="">
                            {subComment.sub_comments.data.map((nestedComment: any, nestedIndex: number) => (
                              <Comment
                                replyingTo={replyingTo!}
                                onReplyClick={() => handleReplyClick(nestedComment.id)}
                                key={nestedComment.id}
                                comment={nestedComment}
                                index={nestedIndex}
                                submitReply={(data: any) => {
                                  requestReplyComment(data);
                                  handleReplyClick(-1);
                                }}
                                onClikcReact={() =>
                                  requestReactionComment({
                                    comment: nestedComment,
                                  })
                                }
                                fetchSubComments={fetchSubComments}
                                isSubComment={true}
                              >
                                {nestedComment.sub_comments?.data?.length > 0 && (
                                  <div className="">
                                    {nestedComment.sub_comments.data.map((nestedComment_f: any, nestedIndex_f: number) => (
                                      <Comment
                                        replyingTo={replyingTo!}
                                        onReplyClick={() => handleReplyClick(nestedComment_f.id)}
                                        key={nestedComment_f.id}
                                        comment={nestedComment_f}
                                        index={nestedIndex_f}
                                        submitReply={(data: any) => {
                                          requestReplyComment(data);
                                          handleReplyClick(-1);
                                        }}
                                        onClikcReact={() =>
                                          requestReactionComment({
                                            comment: nestedComment_f,
                                          })
                                        }
                                        isSubComment={true}
                                      />
                                    ))}
                                  </div>
                                )}
                              </Comment>
                            ))}
                          </div>
                        )}
                      </Comment>
                    ))}
                  </div>
                )}
              </Comment>
            ))}
          </div>

          <div className="flex flex-row  dark:bg-background  justify-center items-start space-x-2 w-full pt-2 px-4 mb-2">
            <ImageUrlProfile isLinkProfile={true} user={Service?.userAuth} className="cursor-pointer rounded-full w-8 h-8" alt="logo" />
            <div className="flex flex-col space-y-2  w-full items-start justify-start">
              <div className="border  relative p-2 rounded-2xl w-full dark:bg-[#3A3B3C] ">
                <div className="flex flex-col">
                  <Textarea
                    value={textComment}
                    onKeyDown={onHandleKeyDown}
                    maxLength={100}
                    onChange={(e) => setTextComment(e.target.value)}
                    className="border border-none min-h-[10px]  focus-visible:ring-0 focus-visible:ring-offset-0 w-full  px-0 py-0 rounded-none dark:bg-[#3A3B3C]"
                    placeholder="Add comment"
                  />
                  <div className="flex justify-between items-center ">
                    <div className="flex space-x-2 items-center">
                      <Popover>
                        <PopoverTrigger asChild>
                          <div className="flex w-8 h-8 rounded-full  bg-gray-200 text-center items-center justify-center">
                            <FontAwesomeIcon icon={faFaceSmileBeam} className="text-gray-500 dark:text-gray-500 cursor-pointer size-5" onClick={() => {}} />
                          </div>
                        </PopoverTrigger>
                        <PopoverContent className="w-92">
                          <div ref={emojiPickerRef} className="absolute bottom-[0px] left-0 z-[100px]" spellCheck={false}>
                            <EmojiPicker reactionsDefaultOpen={false} onEmojiClick={onEmojiClick} emojiStyle={EmojiStyle.FACEBOOK} searchDisabled={true} skinTonesDisabled={true} />
                          </div>
                        </PopoverContent>
                      </Popover>
                      <Popover>
                        <PopoverTrigger asChild>
                          <div className="flex w-8 h-8 rounded-full  bg-gray-200 text-center items-center justify-center">
                            <Sticker className=" text-gray-700 dark:text-gray-500 size-5 font-extrabold cursor-pointer" onClick={() => setShowStickerPicker(true)} />
                          </div>
                        </PopoverTrigger>
                        <PopoverContent className="w-92 bg-transparent border-none shadow-none">
                          {showStickerPicker && (
                            <StickerPicker
                              stickerPickerRef={stickerPickerRef}
                              onStickerSelect={(sticker) => {
                                setShowStickerPicker(false);
                                setSticker(sticker);
                              }}
                            />
                          )}
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="flex w-8 h-8 rounded-full  bg-gray-200 text-center items-center justify-center ">
                      <SendHorizontalIcon
                        className={`cursor-pointer size-5 ${textComment || Object.keys(sticker).length !== 0 ? "text-yellow-400" : "text-gray-500 dark:text-gray-500"}`}
                        onClick={
                          !textComment && Object.keys(sticker).length === 0
                            ? () => {}
                            : () => {
                                requestSubmitComment(sticker, textComment);
                                setTextComment("");
                                setSticker({});
                              }
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
              {Object.keys(sticker).length !== 0 ? (
                <div className="relative sticker-image w-16 h-16">
                  <img src={sticker.thumbnail_url} alt={sticker.pack_name} />
                  <div
                    className="absolute bg-gray-200 hover:bg-gray-300 -top-1 -right-2 w-5 h-5 rounded-full dark:bg-[#3A3B3C] flex items-center cursor-pointer justify-center hover:dark:bg-[#4E4F50]"
                    onClick={() => setSticker({})}
                  >
                    <X className="w-4 h-4 " />
                  </div>
                </div>
              ) : (
                <></>
              )}
            </div>
          </div>
        </div>
      );
    } else {
      return <></>;
    }
  };

  const postContents = (post: any) => {
    if (post) {
      if (post?.media_type === "photo") {
        return (
          <div
            key={post?.post_attachments[0].id}
            style={{
              aspectRatio: `${post?.post_attachments[0]?.attachment_media_width ?? 16} / ${post?.post_attachments[0]?.attachment_media_height ?? 9}`,
            }}
          >
            <div className="flex justify-center items-center">
              <ArrowUpDown value={currentIndex} handleDown={() => onHandleDown()} handleUp={() => onHandleUp()} isDown={currentIndex === posts.length - 1 ? true : false} />
              {post?.sound_url && post?.sound_url !== "" ? (
                <div className="absolute max-sm:bottom-3 max-sm:right-3 bottom-5 right-8  space-x-2  text-white select-none z-50">
                  <AudioPlayer src={post?.sound_url} title={post?.sound_title ?? ""} />
                </div>
              ) : (
                <></>
              )}
              <PhotoPostContent
                post={post?.post_attachments[0]}
                // onOpenPostOption={onOpenPostOption}
              />
            </div>
          </div>
        );
      } else if (post?.media_type === "album") {
        return (
          <div className="flex justify-center items-center ">
            <ArrowUpDown value={currentIndex} handleDown={() => onHandleDown()} handleUp={() => onHandleUp()} isDown={currentIndex === posts.length - 1 ? true : false} />
            {post?.sound_url && post?.sound_url !== "" ? (
              <div className="absolute max-sm:bottom-3 max-sm:right-3 bottom-5 right-8  space-x-2  text-white select-none z-50">
                <AudioPlayer src={post?.sound_url} title={post?.sound_title ?? ""} />
              </div>
            ) : (
              <></>
            )}
            <AlbumPostContent post={post} />
          </div>
        );
      } else if (post?.media_type === "video") {
        return (
          <div className="flex justify-center items-center">
            <ArrowUpDown value={currentIndex} handleDown={() => onHandleDown()} handleUp={() => onHandleUp()} isDown={currentIndex === posts.length - 1 ? true : false} />
            <VideoPostContent
              postAttchment={post}
              videoRefCallback={(el) => {
                videoRef.current = el;
                inViewRef(el);
              }}
            />
            <div ref={inViewRef} className="h-10 flex justify-center items-center"></div>
          </div>
        );
      } else if (post?.media_type === "status") {
        return (
          <div className="flex  justify-center items-center ">
            <ArrowUpDown value={currentIndex} handleDown={() => onHandleDown()} handleUp={() => onHandleUp()} isDown={currentIndex === posts.length - 1 ? true : false} />
            <StatusPostContent post={post} />
          </div>
        );
      } else if (post?.media_type === "live") {
        return (
          <div className="flex w-full h-full  justify-center items-center  ">
            <ArrowUpDown value={currentIndex} handleDown={() => onHandleDown()} handleUp={() => onHandleUp()} isDown={currentIndex === posts.length - 1 ? true : false} />
            {/* <LivePostContent post={post} /> */}
            <LivePostContentFlv post={post} />
          </div>
        );
      }
    } else {
      return <></>;
    }
  };

  return (
    <>
      <div>
        {!postId ? (
          <SearchParamsNotFound params="id" />
        ) : postsRecord.isLoading() ? (
          <ShimmerPostDetail />
        ) : posts && posts.length > 0 ? (
          <div className="flex flex-col lg:grid lg:grid-cols-3  overflow-hidden h-[100vh]">
            <HelmetHtml title="Post-Content" description="This is Post Content" />
            {/* Post Content */}
            <div className="lg:col-span-2 md:h-[60vh] lg:h-auto items-center text-center bg-black ">
              <div
                className="absolute inset-0 bg-cover bg-center bg-black opacity-50 blur-3xl "
                style={{
                  backgroundImage: `url("${posts[currentIndex]?.post_attachments[0]?.attachment_thumbnail_image_file_src}")`,
                }}
              ></div>

              <div className="relative z-50 select-none">
                <X
                  className="absolute max-sm:top-3 top-5 max-sm:left-3 left-5 rounded-full max-sm:size-8 size-11 max-sm:p-1 p-2 bg-opacity-10 text-white cursor-pointer bg-black shadow-md"
                  onClick={onClose}
                />
              </div>

              {posts[currentIndex]?.is_deleted === 1 ? (
                <div className={`relative flex items-center justify-center text-white lg:h-screen h-[60vh] p-6`}>
                  <p className="text-center">
                    We’re sorry, but the content you’re trying to access is currently unavailable. This may be due to one of the following reasons: The content has been removed or
                    The content is temporarily unavailable, or You do not have permission to access the content.
                  </p>
                </div>
              ) : (
                <div className="relative flex items-center justify-center">{postContents(posts[currentIndex])}</div>
              )}
            </div>

            {/* Post Comment */}
            {posts[currentIndex]?.is_deleted === 1 ? <div></div> : postCommentContents(posts[currentIndex])}

            {dialogPostReaction.isOpen && (
              <DialogPostReaction
                post={dialogPostReaction.post}
                isDialogOpen={dialogPostReaction.isOpen}
                postReaction={dialogPostReaction.postReaction}
                setIsDialogOpen={(isOpen) =>
                  setDialogPostReaction({
                    ...dialogPostReaction,
                    isOpen,
                  })
                }
              />
            )}
          </div>
        ) : (
          <SearchParamsNotFound params="id" />
        )}
      </div>

      {dialogBlock.isOpen && (
        <NewFeedDailogBlock
          post={dialogBlock?.post}
          optionKey={dialogBlock?.optionKey}
          isDialogOpen={dialogBlock.isOpen}
          setIsDialogOpen={(isOpen) => setDialogBlock({ ...dialogBlock, isOpen })}
        />
      )}

      {dialogDeletePost.isOpen && (
        <NewFeedDailogDeletePost
          post={dialogDeletePost?.post}
          isDialogOpen={dialogDeletePost.isOpen}
          setIsDialogOpen={(isOpen) => setDialogDeletePost({ ...dialogDeletePost, isOpen })}
        />
      )}

      {dialogReportPost.isOpen && (
        <NewFeedDialogReportPost
          post={dialogReportPost?.post}
          isDialogOpen={dialogReportPost.isOpen}
          setIsDialogOpen={(isOpen) => setDialogReportPost({ ...dialogReportPost, isOpen })}
        />
      )}

      {dialogShare.isOpen && (
        <NewFeedDailogShare
          post={dialogShare.post}
          onSuccess={(post) => onClickedShare(post)}
          isDialogOpen={dialogShare.isOpen}
          setIsDialogOpen={(isOpen) => setDialogShare({ ...dialogShare, isOpen })}
        />
      )}

      {dialogBoostPost.isOpen && (
        <NewFeedDialogBoostPost
          post={dialogBoostPost?.post}
          isDialogOpen={dialogBoostPost.isOpen}
          setIsDialogOpen={(isOpen) => setDialogBoostPost({ ...dialogBoostPost, isOpen })}
        />
      )}

      {postOption.isOpen && (
        <PostOption
          isOpen={postOption.isOpen}
          onOpenChange={(_) => onClosePostOption()}
          post={postOption.post}
          // {...(window.innerWidth > 1500 ? null : { buttonRef: postOption.buttonRef })}
          // buttonRef={window.innerWidth > 1280 ? null : postOption.buttonRef}
          buttonRef={null}
          handleOptionClick={(post, optionKey) => {
            if (optionKey === "share") {
              onOpenDialogShare(post);
            } else if (optionKey === "block_user") {
              onOpenDialogBlock(post, optionKey);
            } else if (optionKey === "boring_user") {
              onOpenDialogBlock(post, optionKey);
            } else if (optionKey === "delete") {
              onOpenDialogDeletePost(post);
            } else if (optionKey === "report") {
              onOpenDialogReportPost(post);
            } else if (optionKey === "boost_post") {
              onOpenDialogBoostPost(post);
            }
          }}
        />
      )}
    </>
  );
}

